import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Zap, ArrowDown, Camera } from "lucide-react";
import { scrollToId } from "@/App";

const HERO_IMG = "https://images.unsplash.com/photo-1561000297-a1bf07a85fc2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwZnVybml0dXJlJTIwcmVtb3ZhbCUyMGNsZWFyYW5jZXxlbnwwfHx8YmxhY2tfYW5kX3doaXRlfDE3OTI0MzQwMjd8MA&ixlib=rb-4.1.0&q=85";
const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section ref={ref} className="grain relative overflow-hidden pt-[72px]" data-testid="hero-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-5 pb-16 pt-14 sm:pt-20 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:pb-24">
        <div className="relative z-10 flex flex-col justify-center lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-8 inline-flex w-fit items-center gap-2 border border-safety/40 bg-safety/10 px-4 py-1.5"
            data-testid="hero-status-pill"
          >
            <Zap className="h-3.5 w-3.5 text-safety" />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-safety">Offerte binnen 2 uur</span>
          </motion.div>
          <h1 className="font-display text-[17vw] font-black uppercase leading-[0.88] tracking-tight sm:text-7xl lg:text-[6.5rem]" data-testid="hero-title">
            <Line delay={0.2}>Jouw afval.</Line>
            <Line delay={0.34}>Onze zorg.</Line>
            <Line delay={0.48} className="text-brand">Rap weg.</Line>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.62 }}
            className="mt-6 font-display text-2xl font-extrabold uppercase tracking-wide text-safety sm:text-3xl"
            data-testid="hero-tagline"
          >
            Geen rommel. <span className="text-paper">Geen gedoe.</span>
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-8 max-w-xl text-base text-muted2 sm:text-lg"
            data-testid="hero-subtitle"
          >
            Meubels, volledige woningontruiming, tuinafval of recyclage — stuur een foto,
            ontvang een eerlijke prijs en wij komen alles ophalen. Jij hoeft nergens aan te komen.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("offerte")}
              data-testid="hero-quote-cta"
              className="group flex items-center gap-3 bg-brand px-8 py-4 font-display text-xl font-bold uppercase tracking-wide text-ink transition-colors duration-200 hover:bg-safety"
            >
              <Camera className="h-5 w-5" />
              Vraag gratis offerte
            </button>
            <button
              onClick={() => scrollToId("diensten")}
              data-testid="hero-services-cta"
              className="flex items-center gap-2 border border-white/20 px-8 py-4 font-display text-xl font-bold uppercase tracking-wide text-paper transition-colors duration-200 hover:border-safety hover:text-safety"
            >
              Bekijk diensten
              <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-4"
            data-testid="hero-stats"
          >
            {[["95%", "gerecycleerd & hergebruikt"], ["24u", "ophaling mogelijk"], ["100%", "actief in heel België"]].map(([v, l]) => (
              <div key={l}>
                <div className="font-display text-3xl font-black text-safety">{v}</div>
                <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted2">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>
        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: EASE }}
            className="clip-frame relative aspect-[4/5] overflow-hidden border border-white/10"
            data-testid="hero-image-frame"
          >
            <motion.img
              src={HERO_IMG}
              alt="Meubels en inboedel klaar voor ophaling"
              style={{ y: imgY }}
              className="h-[115%] w-full object-cover grayscale-[0.4] contrast-125"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 border border-white/20 bg-ink/80 px-4 py-2 backdrop-blur-md" data-testid="hero-photo-badge">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-paper">Foto sturen = prijs weten</span>
            </div>
          </motion.div>
          <div className="hazard-stripes absolute -bottom-4 -right-2 hidden h-4 w-40 lg:block" aria-hidden="true" />
        </div>
      </div>
    </section>
  );
}
